import { useEffect, useState } from 'react';
import { Box, ScreenClassProvider, SiteContent, SiteMain } from '@vp/swan';
import { useLogger, useSwanStyleKeys, useUserContext } from '@vp/ubik-context';
import { FragmentLogger } from '@vp/ubik-logging';
import i18n, { Resource } from 'i18next';
import {
    AUTH_INTERNAL_CLIENT_PARAM,
    AuthDevMode,
    AuthOptions,
    IdentityProvider,
} from '@99designs/design-services-common';
import {
    FeatureFlagProvider,
    VistaExperiment,
    VistaExperimentsProvider,
    featureFlagsFromUbikUserContext,
    vistaExperimentsFromUbikUserContext,
} from '@99designs/experiments';
import { __ } from '@99designs/i18n';
import { Content } from '../components/content';
import { LOGGER_FRAGMENT_ID } from '../lib/consts';

export interface Props {
    localeReference: string;
    resources: Resource;
    locale: string;
    environment?: string;
}

const enableDebug = !!process.env['ENABLE_TRANSLATION_DEBUG'] || false;

const authDevMode = (): AuthDevMode => {
    const isAuthDevMode = process.env.ENVIRONMENT === 'dev';
    if (!isAuthDevMode) {
        return false;
    }

    const clientID = process.env.AUTH0_CLIENT;
    if (!clientID) {
        return true;
    }

    return { clientID, domain: 'oauth.cimpress.io' };
};

export interface ClientOverride {
    developmentMode: AuthDevMode;
    options?: AuthOptions;
}

const INTERNAL_ACCESS_CLIENT: ClientOverride = {
    options: {
        connection: 'vistaprintcom-waad',
    },
    developmentMode: {
        clientID: 'dFzz8qvA9epUe28InBJYrBE4U8RuuVzQ', // This is not a secret, you can find it in the access client URL above.
        // Only set redirectUriOrigin on vista based domains. /auth-callback/ doesn't register when redirecting on localhost
        ...(process.env.NODE_ENV !== 'development' && {
            redirectUriOrigin:
                typeof window !== 'undefined'
                    ? `${window.location.origin}/auth-callback/`
                    : undefined,
        }),
    },
};

function buildAuthConfiguration(): ClientOverride {
    if (typeof window !== 'undefined') {
        const shouldUseInternalAuthClient = new URLSearchParams(window.location.search).get(
            AUTH_INTERNAL_CLIENT_PARAM
        );

        if (shouldUseInternalAuthClient) {
            return INTERNAL_ACCESS_CLIENT;
        }
    }

    return {
        developmentMode: authDevMode(),
    };
}

export const App = ({ resources, environment }: Props) => {
    const logger = useLogger() as FragmentLogger;
    const userContext = useUserContext();
    logger.setContext({ fragmentId: LOGGER_FRAGMENT_ID });

    const [authConfig, setAuthConfig] = useState<ClientOverride>({
        developmentMode: authDevMode(),
    });

    i18n.init({
        resources,
        debug: enableDebug,
        lowerCaseLng: true, //locales are set to lower case e.g. "en-us" not "en-US"
        interpolation: {
            escapeValue: false, // React already provides escaping
        },
        nsSeparator: '*****', // namespace selector is a string delimited which defaults to ":". Overriding to *** so that urls don't get split
        returnObjects: true,
    });
    i18n.changeLanguage(userContext.locale);

    useSwanStyleKeys([
        'core',
        'accordion',
        'alertBox',
        'banner',
        'button',
        'carousel',
        'core',
        'grid',
        'hidden',
        'icon',
        'listbox',
        'modalDialog',
        'tabs',
        'thumbnailsHero',
        'pagination',
        'popover',
        'progressiveImage',
        'selectionSet',
        'spinner',
        'standardTile',
        'toggleSwitch',
        'utility',
        'visible',
        'zoomControls',
        'emptyState',
    ]);

    const experiments: VistaExperiment[] = userContext?.experiments
        ? vistaExperimentsFromUbikUserContext(userContext?.experiments, logger)
        : [];

    const featureFlags = featureFlagsFromUbikUserContext(userContext?.featureFlags);

    useEffect(() => {
        setAuthConfig(buildAuthConfiguration());
    }, []);

    return (
        <VistaExperimentsProvider value={experiments}>
            <FeatureFlagProvider value={featureFlags}>
                <IdentityProvider locale={userContext.locale} {...authConfig}>
                    <ScreenClassProvider>
                        <SiteContent>
                            <SiteMain>
                                <Box paddingBottom="9">
                                    <Content
                                        locale={userContext.locale}
                                        environment={environment}
                                    />
                                </Box>
                            </SiteMain>
                        </SiteContent>
                    </ScreenClassProvider>
                </IdentityProvider>
            </FeatureFlagProvider>
        </VistaExperimentsProvider>
    );
};
